import { useCallback, useEffect, useRef } from 'react';
import { Cookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import { type RootState } from '../../../shared/types';
import {
  DEFAULT_EXPORT_POLLING_INTERVAL_MS,
  resolveExportPollingIntervalMs,
} from '../../../shared/utils/exportPollingInterval';
import {
  useLazyDownloadExportQuery,
  useLazyGetExportStatusQuery,
} from '../data-layer/entReportsApi';
import { entReportsActions } from '../data-store/entReportsSlice';
import formatEntReportType, {
  type EntExportStatusItem,
  type GetExportStatusData,
} from '../types/entReportTypes';

/**
 * useExportStatusPolling Hook
 *
 * Custom hook for polling export status and managing export lifecycle with automatic
 * status checking, progress updates, and completion handling. Manages banner notifications
 * for export success/failure and handles export acknowledgment.
 *
 * @hook
 * @returns {Object} Polling control utilities including:
 *   - startPolling: Function to begin export status polling
 *   - stopPolling: Function to stop export status polling
 *
 * @example
 * const { startPolling, stopPolling } = useExportStatusPolling();
 *
 * // Start polling when exports are active
 * useEffect(() => {
 *   if (activeExports.length > 0) {
 *     startPolling();
 *   } else {
 *     stopPolling();
 *   }
 * }, [activeExports]);
 */
const useExportStatusPolling = () => {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollExportStatusRef = useRef<() => Promise<void>>(async () => {});
  const shownFailureBannersRef = useRef<Set<string>>(new Set());
  const shownSuccessBannersRef = useRef<Set<string>>(new Set());
  const { activeExports } = useTypedSelector(
    (state: RootState) => state.aag.entReports
  );
  const [getExportStatus] = useLazyGetExportStatusQuery();
  const [downloadExport] = useLazyDownloadExportQuery();

  const getCurrentUserId = useCallback(() => {
    const cookies = new Cookies();

    return cookies.get<string>('user_name');
  }, []);

  const downloadFile = useCallback(
    async (exportId: string, reportType: string) => {
      try {
        const blob = await downloadExport(exportId).unwrap();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${formatEntReportType(reportType)}_${
          new Date().toISOString().split('T')[0]
        }.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        Logger.log('error', 'Failed to download export:', error);
      }
    },
    [downloadExport]
  );

  const showSuccessBanner = useCallback((reportType: string) => {
    // De-duplicate by reportType only
    const key = reportType;

    if (shownSuccessBannersRef.current.has(key)) {
      return;
    }

    shownSuccessBannersRef.current.add(key);

    // Suppress top success banner to avoid duplication with stacked banners
  }, []);

  const showErrorBanner = useCallback(
    (reportType: string, exportId?: string, errorMessage?: string) => {
      // De-duplicate by reportType only
      const key = reportType;

      if (shownFailureBannersRef.current.has(key)) {
        return;
      }

      shownFailureBannersRef.current.add(key);

      let errorDescription = '';

      if (errorMessage && errorMessage.trim().length > 0) {
        errorDescription = errorMessage
          .replace(
            /INHERENT_ENTITLEMENT_RISK/g,
            l('Entitlements with Inherent Risk')
          )
          .replace(
            /INHERENT_ENTITLEMENT_USER/g,
            l('Entitlements with Inherent Risk')
          );
      }

      const description =
        errorDescription ||
        `${formatEntReportType(
          reportType
        )} export has failed. Please try again.`;

      dispatch(
        entReportsActions.setExportBanner({
          isVisible: true,
          title: 'Export Failed',
          description,
          colorTheme: 'Critical',
          kind: 'Critical',
          exportId,
        })
      );
    },
    [dispatch, l]
  );

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    dispatch(entReportsActions.setIsPolling(false));

    // Clear the banners tracking when polling stops
    shownFailureBannersRef.current.clear();
    shownSuccessBannersRef.current.clear();
  }, [dispatch]);

  const scheduleNextPoll = useCallback((delayMs: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      pollExportStatusRef
        .current()
        .catch((error) => Logger.log('error', 'Polling failed:', error));
    }, delayMs);
  }, []);

  const pollExportStatus = useCallback(async () => {
    try {
      const userId = getCurrentUserId();
      const response: GetExportStatusData = await getExportStatus(
        userId
      ).unwrap();

      // Filter exports to only process ENT report types
      const entReportTypes = [
        'INHERENT_ENTITLEMENT_RISK',
        'INHERENT_ENTITLEMENT_USER',
      ];
      const entExports: EntExportStatusItem[] = response.exports.filter(
        (exportItem) => entReportTypes.includes(exportItem.reportType)
      );

      // First, process each export status to show appropriate banners
      const seenReportTypes: string[] = [];

      entExports.forEach((exportItem) => {
        const { reportType, status, exportId, errorMessage } = exportItem;
        const progress =
          ((exportItem as { progressPercentage?: number }).progressPercentage ??
            exportItem.progress) ||
          0;

        // Update progress for this report type
        dispatch(
          entReportsActions.setExportProgress({
            reportType,
            progressPercentage: progress,
          })
        );

        if (!seenReportTypes.includes(reportType)) {
          seenReportTypes.push(reportType);
        }

        if (status === 'COMPLETED') {
          // Show success banner for this specific reportType and store exportId for download
          showSuccessBanner(reportType);
          dispatch(
            entReportsActions.setExportIdForReportType({
              reportType,
              exportId,
              status,
            })
          );
          dispatch(entReportsActions.removeActiveExport(reportType));
        } else if (status === 'FAILED') {
          showErrorBanner(reportType, exportId, errorMessage);

          if (exportId) {
            dispatch(
              entReportsActions.setExportIdForReportType({
                reportType,
                exportId,
                status,
              })
            );
          }

          dispatch(entReportsActions.removeActiveExport(reportType));
        }
      });

      // Check if there are any ENT exports still in progress
      const entInProgressExports: EntExportStatusItem[] = entExports.filter(
        (exportItem) =>
          exportItem.status === 'PROCESSING' || exportItem.status === 'QUEUED'
      );

      const shouldStopPolling = entInProgressExports.length === 0;

      // After processing statuses, decide whether to stop polling
      if (shouldStopPolling) {
        stopPolling();
      }

      // If API reports no completed exports, clear completed data so stacked success banners disappear
      if (response.completedExports === 0) {
        dispatch(entReportsActions.clearCompletedExports());
      }

      // Track completed exports count for UI logic
      dispatch(
        entReportsActions.setCompletedExportsCount(
          response.completedExports ?? 0
        )
      );

      // Track report types present in response to gate visible banners
      dispatch(entReportsActions.setLastStatusReportTypes(seenReportTypes));

      if (!shouldStopPolling) {
        scheduleNextPoll(resolveExportPollingIntervalMs(entInProgressExports));
      }
    } catch (error) {
      Logger.log('error', 'Failed to poll export status:', error);
      scheduleNextPoll(DEFAULT_EXPORT_POLLING_INTERVAL_MS);
    }
  }, [
    getCurrentUserId,
    getExportStatus,
    showSuccessBanner,
    showErrorBanner,
    dispatch,
    stopPolling,
    scheduleNextPoll,
  ]);

  pollExportStatusRef.current = pollExportStatus;

  const startPolling = useCallback(() => {
    if (timeoutRef.current) {
      return; // Already polling
    }

    scheduleNextPoll(DEFAULT_EXPORT_POLLING_INTERVAL_MS);
  }, [scheduleNextPoll]);

  // Check for existing exports on initial load
  useEffect(() => {
    const checkInitialExports = async () => {
      try {
        const userId = getCurrentUserId();
        const response: GetExportStatusData = await getExportStatus(
          userId
        ).unwrap();

        // Set completed exports count on initial check
        dispatch(
          entReportsActions.setCompletedExportsCount(
            response.completedExports ?? 0
          )
        );

        // If none completed, clear any completed data leftover
        if (response.completedExports === 0) {
          dispatch(entReportsActions.clearCompletedExports());
        }

        // Filter exports to only process ENT report types
        const entReportTypes = [
          'INHERENT_ENTITLEMENT_RISK',
          'INHERENT_ENTITLEMENT_USER',
        ];
        const entExports = response.exports.filter((exportItem) =>
          entReportTypes.includes(exportItem.reportType)
        );

        // If there are ENT exports in progress, start polling
        const entInProgressExports = entExports.filter(
          (exportItem) =>
            exportItem.status === 'PROCESSING' || exportItem.status === 'QUEUED'
        );

        if (entInProgressExports.length > 0) {
          // Add active ENT exports to Redux for tracking
          entInProgressExports.forEach((exportItem) => {
            dispatch(
              entReportsActions.addActiveExport({
                reportType: exportItem.reportType,
                progress: exportItem.progress,
              })
            );
          });
        }
      } catch (error) {
        Logger.log('error', 'Failed to check initial exports:', error);
      }
    };

    checkInitialExports().catch((error) =>
      Logger.log('error', 'Initial export check failed:', error)
    );
  }, [dispatch, getCurrentUserId, getExportStatus]);

  // Start/stop polling based on active exports from Redux
  useEffect(() => {
    if (activeExports.length > 0) {
      dispatch(entReportsActions.setIsPolling(true));
      startPolling();
    } else {
      stopPolling();
    }
  }, [activeExports, startPolling, stopPolling, dispatch]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  return {
    startPolling,
    stopPolling,
    downloadFile,
  };
};

export default useExportStatusPolling;
